/**
 * StatusCalendarPicker — compact date pill with a popover calendar.
 *
 * DEV-1079, Chunk A. Used by:
 *   - StatusSection (read mode): viewer navigates filed status dates.
 *     Only dotted days clickable; empty days are visually muted.
 *   - StatusWriter (write mode): Lead picks a reporting date for the
 *     new status. Today defaults selected. Future dates disabled.
 *     Days that already have a status are disabled (append-only).
 *
 * Pill is the only resting state. Calendar popover opens on click and
 * closes on outside click, Escape, or after a day selection.
 *
 * No external date libraries — vanilla Date arithmetic matches the
 * codebase's existing pattern.
 *
 * Strings localized via `pages:miniProject.status.calendar.*`
 * (Chunk D Phase 1 sweep, 2026-05-22). Day-of-week labels and month
 * labels still come from the browser's locale-aware
 * Date.toLocaleDateString — those are not i18n keys because Intl
 * handles them per browser locale.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import styles from "./StatusCalendarPicker.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

export type StatusCalendarMode = "read" | "write";

interface Props {
  /** Currently selected date as YYYY-MM-DD, or null when no selection. */
  value: string | null;
  /** YYYY-MM-DD dates that have filed status records (drawn with rings). */
  filedDates: string[];
  /** read: only filed days clickable. write: any non-future, non-filed day. */
  mode: StatusCalendarMode;
  /** Fired when the user picks a day. */
  onChange: (isoDate: string) => void;
  /** Optional lower bound (YYYY-MM-DD) — e.g., project start. */
  minDate?: string;
  /** Pill trigger size (Skip 2026-05-28). "sm" (12px text, default) for
   *  StatusWriter / StatusReader; "md" (15px text) for the redesigned
   *  StatusSection header where the pill anchors a "Status as of …"
   *  sentence and needs to read at the same weight as the heading. */
  size?: "sm" | "md";
}

/* ── Date helpers ──────────────────────────────────────────────── */

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Build a YYYY-MM-DD string from a Date in local time. */
function isoFromDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Parse YYYY-MM-DD into a Date at local midnight. */
function dateFromIso(iso: string): Date {
  const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
  return new Date(y, (m || 1) - 1, d || 1);
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, months: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + months, 1);
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatPillLabel(iso: string | null, locale: string): string {
  if (!iso) return "—";
  try {
    const d = dateFromIso(iso);
    return d.toLocaleDateString(locale || undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatMonthLabel(d: Date, locale: string): string {
  return d.toLocaleDateString(locale || undefined, {
    year: "numeric",
    month: "long",
  });
}

/* ── Component ─────────────────────────────────────────────────── */

const StatusCalendarPicker: React.FC<Props> = ({
  value,
  filedDates,
  mode,
  onChange,
  minDate,
  size = "sm",
}) => {
  const { t, i18n } = useAppTranslation("pages");
  // Use the i18n-active language so date formatting matches the user's
  // locale preference, with a graceful fallback to the browser default.
  const locale = i18n.language || (typeof navigator !== "undefined" ? navigator.language : "en");

  // ── Open / close state ──
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // ── Visible month ──
  // Defaults to month of selected date, or current month, or today.
  const initialMonth = useMemo(() => {
    if (value) return startOfMonth(dateFromIso(value));
    return startOfMonth(new Date());
  }, [value]);
  const [viewMonth, setViewMonth] = useState<Date>(initialMonth);

  // Reset view month when the popover opens — always center on the
  // selected date so the user lands where they expect.
  useEffect(() => {
    if (open) {
      setViewMonth(value ? startOfMonth(dateFromIso(value)) : startOfMonth(new Date()));
    }
  }, [open, value]);

  // Close on outside click and on Escape.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  // ── Filed days as a Set for O(1) lookup ──
  const filedSet = useMemo(() => new Set(filedDates), [filedDates]);

  // ── Grid: render 6 weeks × 7 days, starting on Sunday of the week
  //         containing the 1st of viewMonth. Cells outside viewMonth
  //         are muted but still rendered for a stable grid. ──
  const grid = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const startWeekday = monthStart.getDay(); // 0=Sun
    const gridStart = new Date(monthStart);
    gridStart.setDate(monthStart.getDate() - startWeekday);
    const cells: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [viewMonth]);

  // ── Per-day classification ──
  const today = useMemo(() => new Date(), []);
  const todayIso = useMemo(() => isoFromDate(today), [today]);
  const minDateObj = useMemo(() => (minDate ? dateFromIso(minDate) : null), [minDate]);

  const classifyDay = useCallback(
    (d: Date): {
      iso: string;
      inMonth: boolean;
      filed: boolean;
      isToday: boolean;
      isSelected: boolean;
      clickable: boolean;
    } => {
      const iso = isoFromDate(d);
      const inMonth = d.getMonth() === viewMonth.getMonth();
      const filed = filedSet.has(iso);
      const isToday = isSameDay(d, today);
      const isSelected = !!value && iso === value;

      let clickable: boolean;
      if (mode === "read") {
        // Only filed days are meaningful in read mode.
        clickable = filed;
      } else {
        // Write mode: no future dates, no already-filed dates,
        // honor minDate if set.
        const isFuture = d.getTime() > today.getTime() && !isSameDay(d, today);
        const beforeMin = minDateObj ? d.getTime() < minDateObj.getTime() : false;
        clickable = !isFuture && !beforeMin && !filed;
      }
      return { iso, inMonth, filed, isToday, isSelected, clickable };
    },
    [viewMonth, filedSet, value, today, mode, minDateObj],
  );

  // ── Handlers ──
  const togglePopover = useCallback(() => setOpen((v) => !v), []);
  const goPrevMonth = useCallback(() => setViewMonth((m) => addMonths(m, -1)), []);
  const goNextMonth = useCallback(() => setViewMonth((m) => addMonths(m, 1)), []);

  const handleDayClick = useCallback(
    (iso: string) => {
      onChange(iso);
      setOpen(false);
    },
    [onChange],
  );

  // ── Render ──
  const dayLabels = useMemo(() => {
    // Sunday-first short labels per locale.
    const base = new Date(2024, 0, 7); // a Sunday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      return d.toLocaleDateString(locale || undefined, { weekday: "narrow" });
    });
  }, [locale]);

  return (
    <div className={styles.wrap}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.pill} ${size === "md" ? styles.pillMd : ""}`}
        onClick={togglePopover}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t("miniProject.status.calendar.pickAria")}
      >
        <svg
          className={styles.pillIcon}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span className={styles.pillLabel}>{formatPillLabel(value, locale)}</span>
        <svg
          className={styles.pillChevron}
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          ref={popoverRef}
          className={styles.popover}
          role="dialog"
          aria-label={t("miniProject.status.calendar.popoverAria")}
        >
          <div className={styles.popHeader}>
            <button
              type="button"
              className={styles.navBtn}
              onClick={goPrevMonth}
              aria-label={t("miniProject.status.calendar.prevMonth")}
            >
              ‹
            </button>
            <span className={styles.popMonth}>{formatMonthLabel(viewMonth, locale)}</span>
            <button
              type="button"
              className={styles.navBtn}
              onClick={goNextMonth}
              aria-label={t("miniProject.status.calendar.nextMonth")}
            >
              ›
            </button>
          </div>

          <div className={styles.dayHeader}>
            {dayLabels.map((label, i) => (
              <div key={i} className={styles.dayHeaderCell}>
                {label}
              </div>
            ))}
          </div>

          <div className={styles.grid}>
            {grid.map((d) => {
              const c = classifyDay(d);
              const cls = [
                styles.day,
                !c.inMonth ? styles.dayMuted : "",
                c.isToday ? styles.dayToday : "",
                c.filed ? styles.dayFiled : "",
                c.isSelected ? styles.daySelected : "",
                !c.clickable ? styles.dayDisabled : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <button
                  key={c.iso}
                  type="button"
                  className={cls}
                  onClick={c.clickable ? () => handleDayClick(c.iso) : undefined}
                  disabled={!c.clickable}
                  aria-pressed={c.isSelected}
                  aria-label={c.iso}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          <div className={styles.legend}>
            <span className={styles.legendItem}>
              <span className={styles.legendDotFiled} aria-hidden="true" />
              {t("miniProject.status.calendar.legend.filed")}
            </span>
            {value && (
              <span className={styles.legendItem}>
                <span className={styles.legendDotSelected} aria-hidden="true" />
                {t("miniProject.status.calendar.legend.viewing")}
              </span>
            )}
            {mode === "write" && (
              <span className={styles.legendItem} style={{ marginInlineStart: "auto" }}>
                <span style={{ color: "var(--text-muted)" }}>{todayIso}</span>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusCalendarPicker;
